import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { USER_ROLES } from "../app/app.constants";
import { useAppDispatch } from "../app/app.store";
import {
  useGetPermissionsQuery,
  useGetProfileQuery,
  useGetSubscriptionQuery,
  useVerifySubscriptionQuery,
} from "../app/features/auth/auth.api";
import { userLoggedOut, verifyUser } from "../app/features/auth/auth.slice";
import { getErrorMessage } from "../lib/error";
import { toast } from "../lib/toast";
import useAuth from "./useAuth";
import useError from "./useError";

interface StoredAuthData {
  verifiedInfo: string;
  userId: string;
  accessToken: string;
}

const KNOWN_ROLES = new Set(Object.values(USER_ROLES));

/** Read auth credentials from localStorage. Returns null if anything is missing. */
function readStoredAuth(): StoredAuthData | null {
  const verifiedInfo = localStorage.getItem("verifiedInfo");
  const userId = localStorage.getItem("userId");
  const accessToken = localStorage.getItem("access_token");

  if (!verifiedInfo || !userId || !accessToken) return null;
  return { verifiedInfo, userId, accessToken };
}

/**
 * useAppBoot
 *
 * Orchestrates the full application bootstrap sequence:
 *   1. Restore auth session from localStorage
 *   2. Fetch & verify the organization's subscription
 *   3. Fetch the user profile
 *   4. Fetch user permissions and validate access
 *   5. Redirect to onboarding when the subscription is inactive
 */
export default function useAppBoot() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userId, role, verifiedInfo } = useAuth();

  const [isInitialized, setIsInitialized] = useState(false);

  // ── 1. Restore session ────────────────────────────────────────────────────
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    try {
      const stored = readStoredAuth();
      if (stored) {
        dispatch(
          verifyUser({
            access_token: stored.accessToken,
            user: JSON.parse(stored.verifiedInfo),
          }),
        );
      }
    } catch {
      // Corrupted storage — start fresh
      localStorage.clear();
      dispatch(userLoggedOut());
    } finally {
      setIsInitialized(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Guard: reject unknown roles immediately
  useEffect(() => {
    if (!role) return;
    if (!KNOWN_ROLES.has(role)) {
      toast("error", "You are not authorized.");
      dispatch(userLoggedOut());
    }
  }, [role, dispatch]);

  // ── 2. Subscription ───────────────────────────────────────────────────────

  const {
    data: subscriptionData,
    isLoading: isSubscriptionLoading,
    error: subscriptionError,
    isError: isSubscriptionError,
  } = useGetSubscriptionQuery(String(userId), {
    skip: !userId,
  });

  useError(
    subscriptionError,
    getErrorMessage(subscriptionError, "Failed to fetch subscription details"),
  );

  const organizationId = useMemo(
    () => subscriptionData?.data?.[0]?.organization_id,
    [subscriptionData],
  );

  const {
    data: verificationData,
    isLoading: isVerificationLoading,
    error: verificationError,
    isError: isVerificationError,
  } = useVerifySubscriptionQuery(String(organizationId), {
    skip: !organizationId || !verifiedInfo,
  });

  useError(
    verificationError,
    getErrorMessage(verificationError, "Failed to verify subscription"),
  );

  // Any subscription error → send user to onboarding
  useEffect(() => {
    const isLoading = isSubscriptionLoading || isVerificationLoading;
    const hasError = isSubscriptionError || isVerificationError;

    if (hasError && !isLoading) {
      navigate("/onboard", { replace: true });
    }
  }, [
    navigate,
    isSubscriptionError,
    isVerificationError,
    isSubscriptionLoading,
    isVerificationLoading,
  ]);

  // Derive subscription status from the verification response.
  const { isSubscribedHRX, isSubscriptionVerified } = useMemo(() => {
    const isSubscribedHRX = verificationData?.data?.isSubscribedHRX ?? false;
    const isSubscriptionVerified = !isVerificationLoading && isSubscribedHRX;
    return { isSubscribedHRX, isSubscriptionVerified };
  }, [verificationData?.data?.isSubscribedHRX, isVerificationLoading]);

  // Send the user to onboarding if their subscription is inactive.
  const redirectIfNotSubscribed = useCallback(() => {
    if (!isVerificationLoading && verificationData && !isSubscribedHRX) {
      navigate("/onboard", { replace: true });
    }
  }, [isSubscribedHRX, navigate, isVerificationLoading, verificationData]);

  useEffect(() => {
    if (isInitialized && verifiedInfo) redirectIfNotSubscribed();
  }, [isInitialized, verifiedInfo, redirectIfNotSubscribed]);

  // ── 3. User profile ───────────────────────────────────────────────────────

  const {
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    isSuccess: isProfileLoaded,
    error: profileError,
  } = useGetProfileQuery(String(userId), {
    skip: !userId || !isSubscriptionVerified,
  });

  useError(
    profileError,
    getErrorMessage(profileError, "Failed to fetch user profile"),
  );

  const isProfileReady = !isProfileLoading && isProfileLoaded;

  // ── 4. Permissions ────────────────────────────────────────────────────────

  const permissionQueryArg = useMemo(
    () => ({ userId: String(userId) }),
    [userId],
  );

  const {
    data: permissionData,
    isLoading: isPermissionLoading,
    error: permissionError,
  } = useGetPermissionsQuery(permissionQueryArg, {
    skip: !userId || !isSubscriptionVerified || !isProfileReady,
  });

  useError(
    permissionError,
    getErrorMessage(permissionError, "Failed to fetch user permissions"),
  );

  // Check Permissions
  useEffect(() => {
    // Evaluators skip the permission check;
    if (
      !role ||
      isPermissionLoading ||
      role === USER_ROLES.EVALUATOR ||
      role === USER_ROLES.LINE_MANAGER ||
      role === USER_ROLES.USER ||
      role === USER_ROLES.EVALUATOR_USER ||
      role === USER_ROLES.EVALUATOR_MANAGER
    ) {
      return;
    }

    // Everyone else must have at least one permission
    if (!(permissionData?.success && permissionData.data?.length > 0)) {
      dispatch(userLoggedOut());
      console.log("You have no access.", role);
      toast("error", "You have no access.");
    }
  }, [permissionData, isPermissionLoading, role, dispatch]);

  // ── 5. Derived values ─────────────────────────────────────────────────────

  const isAppLoading =
    !isInitialized ||
    isSubscriptionLoading ||
    isVerificationLoading ||
    isProfileLoading ||
    isProfileFetching ||
    isPermissionLoading;

  return {
    isSubscriptionVerified,
    isLoading: isAppLoading,
  };
}
